import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';
import type { MediaCategory, Movie, NewsItem } from '@/types/media';

export function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function formatDate(date: string): string {
  const [y, m, d] = date.split('-');
  if (!y || !m || !d) return date;
  return `${y}年${Number(m)}月${Number(d)}日`;
}

export function getAllGenres(): string[] {
  const set = new Set<string>();
  movies.forEach((m) => m.genre.forEach((g) => set.add(g)));
  return Array.from(set);
}

export function getAllYears(): number[] {
  const set = new Set<number>();
  movies.forEach((m) => set.add(m.year));
  return Array.from(set).sort((a, b) => b - a);
}

export function getAllRegions(): string[] {
  const set = new Set<string>();
  movies.forEach((m) => set.add(m.region));
  return Array.from(set);
}

export function getAllNewsTypes(): string[] {
  const set = new Set<string>();
  news.forEach((n) => set.add(n.type));
  return Array.from(set);
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getNewsById(id: string): NewsItem | undefined {
  return news.find((n) => n.id === id);
}

export function getMoviesByCategory(category: MediaCategory): Movie[] {
  return movies.filter((m) => m.category === category);
}

export function getLatestMovies(category: MediaCategory, count: number): Movie[] {
  return movies
    .filter((m) => m.category === category)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, count);
}

export function getHotMovies(count: number): Movie[] {
  return movies
    .filter((m) => m.hot)
    .sort((a, b) => b.views - a.views)
    .slice(0, count);
}

export function getHotMoviesFallback(count: number): Movie[] {
  return movies.slice().sort((a, b) => b.views - a.views).slice(0, count);
}

export function getRelatedMovies(movie: Movie, count: number): Movie[] {
  const sameGenre = movies.filter(
    (m) => m.id !== movie.id && m.genre.some((g) => movie.genre.includes(g)),
  );
  const sameActor = movies.filter(
    (m) => m.id !== movie.id && m.actors.some((a) => movie.actors.includes(a)),
  );
  const merged = [...sameActor, ...sameGenre].filter(
    (m, i, arr) => arr.findIndex((x) => x.id === m.id) === i,
  );
  if (merged.length >= count) return merged.slice(0, count);
  const rest = movies.filter((m) => m.id !== movie.id && !merged.some((x) => x.id === m.id));
  return [...merged, ...rest].slice(0, count);
}

export function getLatestNews(count: number): NewsItem[] {
  return news.slice().sort((a, b) => b.publishTime.localeCompare(a.publishTime)).slice(0, count);
}

export function getRelatedNews(item: NewsItem, count: number): NewsItem[] {
  const sameType = news.filter((n) => n.id !== item.id && n.type === item.type);
  const others = news.filter((n) => n.id !== item.id && n.type !== item.type);
  return [...sameType, ...others].slice(0, count);
}

export type SortKey = 'updated' | 'newest' | 'rating' | 'popular';

export function sortMovies(list: Movie[], sort: SortKey): Movie[] {
  const arr = list.slice();
  switch (sort) {
    case 'newest':
      return arr.sort((a, b) => b.year - a.year || b.updatedAt.localeCompare(a.updatedAt));
    case 'rating':
      return arr.sort((a, b) => b.rating - a.rating);
    case 'popular':
      return arr.sort((a, b) => b.views - a.views);
    case 'updated':
    default:
      return arr.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
}

export interface MovieFilterOptions {
  category?: MediaCategory;
  genres?: string[];
  years?: number[];
  regions?: string[];
  keyword?: string;
  sort?: SortKey;
}

export function filterMovies(options: MovieFilterOptions): Movie[] {
  const { category, genres = [], years = [], regions = [], keyword, sort = 'updated' } = options;
  let list = movies.slice();

  if (category) list = list.filter((m) => m.category === category);
  if (genres.length) list = list.filter((m) => m.genre.some((g) => genres.includes(g)));
  if (years.length) list = list.filter((m) => years.includes(m.year));
  if (regions.length) list = list.filter((m) => regions.includes(m.region));
  if (keyword && keyword.trim()) {
    const kw = keyword.trim().toLowerCase();
    list = list.filter(
      (m) =>
        m.title.toLowerCase().includes(kw) ||
        m.actors.some((a) => a.toLowerCase().includes(kw)) ||
        m.director.toLowerCase().includes(kw),
    );
  }

  return sortMovies(list, sort);
}

export function searchMovies(keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return movies.filter((m) => m.title.toLowerCase().includes(kw));
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function totalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}